/**
 * CompareView - Modal de comparação ANTES vs AGORA
 * 
 * - Card expande
 * - Tela divide em duas colunas
 * - Mostra trecho antigo vs atual
 */

import React from 'react';
import { X } from 'lucide-react';

export default function CompareView({
  pastPhase,
  currentPhase,
  isOpen = false,
  onClose,
}) {
  if (!isOpen || !pastPhase) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        style={{ animation: 'fadeIn 0.3s ease-out' }}
      />

      {/* Modal */}
      <div 
        className="relative w-full max-w-5xl max-h-[85vh] overflow-auto rounded-2xl shadow-2xl"
        style={{
          background: 'linear-gradient(145deg, #faf8f5 0%, #f5f0e8 100%)',
          animation: 'slideUp 0.3s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white text-stone-500 hover:text-stone-700 transition-colors z-10"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="text-center pt-8 pb-4 px-8 border-b border-stone-200/50">
          <h2 className="text-2xl font-serif text-stone-800 mb-2">
            Sua Evolução
          </h2>
          <p className="text-sm text-stone-500">
            Veja como sua escrita se transformou ao longo do tempo
          </p>
        </div>

        {/* Comparison columns */}
        <div className="grid md:grid-cols-2 gap-0 md:gap-px bg-stone-200/30">
          {/* ANTES (Past) */}
          <div className="bg-gradient-to-br from-stone-50 to-stone-100/50 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 text-xs font-medium tracking-wider uppercase rounded-full bg-stone-200 text-stone-600">
                {pastPhase.period}
              </span>
              <span className="text-xs text-stone-400">ANTES</span>
            </div>

            <h3 className="text-lg font-serif text-stone-700 mb-3">
              {pastPhase.title}
            </h3>

            <div className="mb-4">
              <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">
                Estilo
              </p>
              <p className="text-sm text-stone-600">
                {pastPhase.writingStyle}
              </p>
            </div>

            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
            >
              <p className="text-sm font-serif text-stone-600 leading-relaxed italic">
                "{pastPhase.excerpt}"
              </p>
            </div>
          </div>

          {/* AGORA (Current) */}
          <div className="bg-gradient-to-br from-amber-50/50 to-orange-50/30 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 text-xs font-medium tracking-wider uppercase rounded-full bg-amber-100 text-amber-700">
                {currentPhase?.period || 'Hoje'}
              </span>
              <span className="text-xs text-amber-600">AGORA</span>
            </div>

            <h3 className="text-lg font-serif text-stone-700 mb-3">
              {currentPhase?.title || 'Momento Atual'}
            </h3>

            <div className="mb-4">
              <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">
                Estilo
              </p>
              <p className="text-sm text-stone-600">
                {currentPhase?.writingStyle || 'Sua escrita continua evoluindo...'}
              </p>
            </div>

            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'rgba(251, 191, 36, 0.08)' }}
            >
              <p className="text-sm font-serif text-stone-600 leading-relaxed italic">
                "{currentPhase?.excerpt || 'Continue escrevendo para ver sua evolução...'}"
              </p>
            </div>
          </div>
        </div>

        {/* Footer insight */}
        <div className="text-center py-6 px-8 border-t border-stone-200/50">
          <p className="text-sm text-stone-500 flex items-center justify-center gap-2">
            <span className="text-amber-500">✨</span>
            Cada palavra escrita é um passo na sua jornada
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
